import { ChangeDetectorRef, Component, DestroyRef, Injector, OnInit, inject, input } from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { DomSanitizer, Meta, SafeHtml, Title } from '@angular/platform-browser';
import { ActivatedRouteSnapshot, ActivationStart, Router } from '@angular/router';

import { EventModel } from '@frontend/sports/betting-offer/feature/model';
import { SeoConfig, Sitecore } from '@frontend/sports/common/client-config-data-access';
import { RouteTag } from '@frontend/sports/common/core/data-access/route';
import { isDefined } from '@frontend/sports/common/core/utils/extended-types';
import { RouterEventsService } from '@frontend/sports/common/core/utils/router-events';
import { DeviceService, MediaQueryService } from '@frontend/vanilla/core';
import { IconCustomComponent } from '@frontend/vanilla/shared/icons';
import { assign, includes } from 'lodash-es';
import { Observable, distinctUntilChanged, filter, from, map, switchMap } from 'rxjs';

import { SeoContentService } from './seo-content.service';
import { SeoContentApiResponse } from './seo.models';

@Component({
    selector: 'ms-content-box',
    templateUrl: 'content-box.component.html',
    imports: [IconCustomComponent],
})
export class ContentBoxComponent implements OnInit {
    routeChanging = false;
    header: string;
    primaryHeader: boolean;
    content: SafeHtml;
    collapsed = false;
    userClicked = false;
    isPartialCollapsible: boolean;
    private readonly tagName = 'description';
    private readonly destroyRef = inject(DestroyRef);

    readonly event = input<EventModel | undefined>();

    constructor(
        public sitecore: Sitecore,
        private seoContentService: SeoContentService,
        private routerEventsService: RouterEventsService,
        private router: Router,
        private meta: Meta,
        private title: Title,
        private seoConfig: SeoConfig,
        private changeDetectionRef: ChangeDetectorRef,
        device: DeviceService,
        mediaService: MediaQueryService,
        private _sanitizer: DomSanitizer,
        private injector: Injector,
    ) {
        mediaService
            .observe()
            .pipe(
                filter(() => !this.userClicked && !device.isRobot),
                map((queries) => 'gt-md' in queries.breakpoints && queries.breakpoints['gt-md']),
                distinctUntilChanged(),
                takeUntilDestroyed(),
            )
            .subscribe(() => (this.collapsed = true));

        this.isPartialCollapsible = this.seoConfig.isPartialCollapsibleContentEnabled;
    }

    ngOnInit(): void {
        toObservable(this.event, {
            injector: this.injector,
        })
            .pipe(
                switchMap((event) => {
                    if (!event) {
                        return this.routerEventsService.currentActivationEnd.pipe(
                            filter(isDefined),
                            switchMap((e) => from(this.getSeoContent(e.snapshot))),
                        );
                    }

                    return this.getSeoContentForEvent(event);
                }),
            )
            .pipe(filter(isDefined), takeUntilDestroyed(this.destroyRef))
            .subscribe((seoContent) => this.updateSeoContent(seoContent));

        this.router.events
            .pipe(
                filter((e) => e instanceof ActivationStart),
                distinctUntilChanged(),
                takeUntilDestroyed(this.destroyRef),
            )
            .subscribe(() => (this.routeChanging = true));

        this.meta.addTag({ name: this.tagName });
    }

    toggle(): void {
        this.collapsed = !this.collapsed;
        this.userClicked = true;
    }

    private getSeoContentForEvent(event: EventModel): Observable<SeoContentApiResponse | undefined> {
        return this.seoContentService.getForEventDetails({
            lobby: false,
            live: event.live,
            highlights: false,
            allSports: false,
            sport: event.sport.id,
            region: event.region.id,
            league: event.league?.id,
            event: event.id,
        });
    }

    private getSeoContent(routeSnapshot: ActivatedRouteSnapshot): Observable<SeoContentApiResponse | undefined> {
        this.cleanContent();

        if (routeSnapshot.data.tag === RouteTag.EventDetails) {
            const event = routeSnapshot.data.model.model as EventModel;
            if (event) {
                return this.getSeoContentForEvent(event);
            }
        }

        const params = assign({ path: routeSnapshot.url.map((segment) => `/${segment}`).join('') }, routeSnapshot.params);
        const tagOf = (...sources: RouteTag[]) => includes(sources, routeSnapshot.data.tag);

        return this.seoContentService.getSeoContent(params, {
            liveHighlights: tagOf(RouteTag.LiveHighlights),
            sportsList: tagOf(RouteTag.LiveAll, RouteTag.SportList),
            sportsLobby: tagOf(RouteTag.Lobby),
            virtual: tagOf(RouteTag.VirtualFixture),
            live: tagOf(
                RouteTag.Live,
                RouteTag.LiveAll,
                RouteTag.LiveVideo,
                RouteTag.LiveHighlights,
                RouteTag.LiveSport,
                RouteTag.LiveEsports,
                RouteTag.LiveRegion,
            ),
            liveVideo: tagOf(RouteTag.LiveVideo),
            esportsLobby: tagOf(RouteTag.EsportsLobby),
            esportsHighlights: tagOf(RouteTag.LiveEsports),
            multiSportsLobby: tagOf(RouteTag.MultiSportsLobby),
        });
    }

    private updateSeoContent(seoContent: SeoContentApiResponse): void {
        this.routeChanging = false;

        this.header = seoContent.header || '';
        this.content = seoContent.content ? this._sanitizer.bypassSecurityTrustHtml(seoContent.content) : '';
        this.primaryHeader = !seoContent.h1Included;

        this.title.setTitle(seoContent.metaTitle || '');
        this.meta.updateTag({ name: this.tagName, content: seoContent.metaDescription || '' });
        this.changeDetectionRef.markForCheck();
    }

    private cleanContent(): void {
        this.header = '';
        this.content = '';
    }
}
